import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import API from '../config/api';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const type = searchParams.get('type') || 'reset_password';
  const isSetPassword = type === 'set_password';

  const [tokenValid, setTokenValid] = useState(null); // null = checking, true, false
  const [tokenInfo, setTokenInfo] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  // Verify the token is valid before showing the form
  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      return;
    }
    API.get(`/auth/verify-token/${token}`)
      .then((res) => {
        if (res.data.success) {
          setTokenValid(true);
          setTokenInfo(res.data);
        } else {
          setTokenValid(false);
        }
      })
      .catch(() => setTokenValid(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const res = await API.post('/auth/reset-password', { token, password, confirm_password: confirm });
      if (res.data.success) {
        setDone(true);
        setTimeout(() => navigate('/login'), 3000);
      } else {
        setError(res.data.message || 'Failed to update password.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please request a new link.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'h-[52px] w-full rounded-xl border-2 border-slate-200 bg-white px-4 text-[0.95rem] text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-brand focus:ring-4 focus:ring-brand-light disabled:cursor-not-allowed disabled:bg-slate-100';

  const renderBody = () => {
    // Checking token
    if (tokenValid === null) {
      return (
        <div className="flex flex-col items-center py-8 text-gray-500">
          <span className="mb-3 inline-block h-6 w-6 animate-spin rounded-full border-2 border-solid border-brand border-r-transparent" />
          Verifying link…
        </div>
      );
    }

    // Invalid / expired token
    if (!tokenValid) {
      return (
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <i className="bi bi-x-circle text-3xl text-red-500" />
          </div>
          <h2 className="mb-2 text-xl font-semibold text-gray-800">Link Invalid or Expired</h2>
          <p className="mb-6 text-gray-500 text-sm">
            This link has already been used or has expired. Request a new one below.
          </p>
          {!isSetPassword && (
            <Link to="/forgot-password" className="text-brand hover:text-brand-dark text-sm font-medium no-underline">
              Request new link
            </Link>
          )}
          <div className="mt-4">
            <Link to="/login" className="text-[0.85rem] text-gray-400 no-underline hover:text-brand">
              ← Back to Sign In
            </Link>
          </div>
        </div>
      );
    }

    // Success
    if (done) {
      return (
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <i className="bi bi-check-circle text-3xl text-green-600" />
          </div>
          <h2 className="mb-2 text-xl font-semibold text-gray-800">Password Updated!</h2>
          <p className="text-gray-500 text-sm">Redirecting you to Sign In…</p>
        </div>
      );
    }

    // Form
    return (
      <>
        <h1 className="mb-1 text-[1.6rem] font-semibold text-qure-secondary">
          {isSetPassword ? 'Set Your Password' : 'Reset Password'}
        </h1>
        {tokenInfo?.first_name && (
          <p className="mb-1 text-gray-500 text-sm">Hi, {tokenInfo.first_name}.</p>
        )}
        <p className="mb-7 text-gray-500 text-sm">Choose a strong password for your account.</p>

        {error && (
          <div className="mb-5 flex items-center gap-3 rounded-xl bg-red-50 px-4 py-3 text-red-700 text-sm">
            <i className="bi bi-exclamation-circle-fill shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <input
              type="password"
              className={inputClass}
              placeholder="New password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              minLength={6}
            />
          </div>
          <div className="mb-6">
            <input
              type="password"
              className={inputClass}
              placeholder="Confirm new password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              disabled={loading}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="full-width-btn flex h-12 w-full items-center justify-center gap-2 rounded-[10px] bg-brand text-[0.95rem] font-medium text-white transition hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? (
              <>
                <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent" />
                Saving…
              </>
            ) : (
              <>
                <i className="bi bi-lock-fill" />
                {isSetPassword ? 'Set Password' : 'Reset Password'}
              </>
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link to="/login" className="text-[0.9rem] text-gray-500 no-underline hover:text-brand">
            ← Back to Sign In
          </Link>
        </div>
      </>
    );
  };

  return (
    <div className="relative z-10 flex min-h-screen items-center justify-center bg-white p-6">
      <div className="w-full max-w-[460px] rounded-3xl bg-white p-10 shadow-[0_10px_30px_rgba(0,0,0,0.1)]">
        <img
          src="https://qure.science/images/qure.webp"
          alt="Qure"
          className="mb-8 h-auto w-[140px]"
          style={{ filter: 'invert(44%) sepia(91%) saturate(2044%) hue-rotate(347deg) brightness(99%) contrast(95%)' }}
          onError={(e) => { e.target.src = 'https://via.placeholder.com/140x60?text=Qure'; }}
        />
        {renderBody()}
      </div>
    </div>
  );
};

export default ResetPassword;
